// src/components/ui/UiToaster.tsx
import * as React from "react";
import type { UiToastV1 } from "@/contracts/ui/UiToastBus";
import { UI_TOAST_EVENT } from "@/contracts/ui/UiToastBus";

type LiveToast = UiToastV1 & { _key: string; _at: number };

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export default function UiToaster(): JSX.Element | null {
  const [toasts, setToasts] = React.useState<LiveToast[]>([]);

  React.useEffect(() => {
    const onToast = (e: Event) => {
      const ce = e as CustomEvent<UiToastV1>;
      const t = ce.detail;
      if (!t || t.v !== 1 || !t.message) return;

      const now = Date.now();
      const live: LiveToast = {
        ...t,
        _key: `${t.id ?? "toast"}:${now}:${Math.random().toString(16).slice(2)}`,
        _at: now,
      };

      setToasts((prev) => [live, ...prev].slice(0, 3));

      const ttl = Math.max(800, Math.min(8000, t.ms ?? 2200));
      window.setTimeout(() => {
        setToasts((prev) => prev.filter((x) => x._key !== live._key));
      }, ttl);
    };

    window.addEventListener(UI_TOAST_EVENT, onToast as EventListener);
    return () => window.removeEventListener(UI_TOAST_EVENT, onToast as EventListener);
  }, []);

  if (!toasts.length) return null;

  const toneClass = (tone?: string) => {
    const t = (tone ?? "info").toLowerCase();
    if (t === "ok") return "border-emerald-300/20 bg-emerald-400/10 text-emerald-50";
    if (t === "warn") return "border-amber-300/20 bg-amber-400/10 text-amber-50";
    if (t === "bad") return "border-red-300/20 bg-red-400/10 text-red-50";
    return "border-cyan-300/20 bg-cyan-400/10 text-cyan-50";
  };

  return (
    <div
      className="
        fixed z-[100000]
        top-[calc(var(--app-header-h,64px)+12px)]
        right-3
        flex flex-col gap-2
        pointer-events-none
      "
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((t) => (
        <div
          key={t._key}
          className={cx(
            "min-w-[260px] max-w-[360px]",
            "rounded-2xl border backdrop-blur-xl",
            "bg-black/55 dark:bg-black/60",
            "shadow-[0_18px_60px_rgba(0,0,0,0.45)]",
            "px-4 py-3",
            toneClass(t.tone),
          )}
          role="status"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {t.title ? (
                <div className="text-[12px] font-semibold leading-tight truncate">{t.title}</div>
              ) : null}
              <div className={cx("text-[12px] leading-snug", t.title ? "opacity-90" : "")}>
                {t.message}
              </div>
            </div>

            <div
              className="h-2 w-2 rounded-full mt-1"
              style={{
                background:
                  t.tone === "ok"
                    ? "rgb(52 211 153)"
                    : t.tone === "warn"
                      ? "rgb(251 191 36)"
                      : t.tone === "bad"
                        ? "rgb(248 113 113)"
                        : "rgb(34 211 238)",
              }}
              aria-hidden
            />
          </div>

          {t.source ? <div className="mt-1 text-[10px] opacity-55">src: {t.source}</div> : null}
        </div>
      ))}
    </div>
  );
}
